import { useMemo, useState, useEffect } from "react";
import { Box, Typography, IconButton, Stack, Grid, Tooltip, Divider, Menu, MenuItem, useMediaQuery, useTheme } from "@mui/material";
import { NursePatient } from "../models";
import { getPainIcon } from "src/utils/painIconHelper";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ReactComponent as AddMaster } from "src/assets/icons/pages/nurse/add-master.svg"; // Reusing from index.tsx
import VitalBadge, { VitalBadgeVariant } from "./VitalBadge";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { nurseService } from "src/api/nurse/nurseService";

interface PatientCardProps {
    data: NursePatient;
    onAction: (action: string, row: NursePatient) => void;
    t: (key: string) => string;
    isolationIconsMap: Record<number, string>;
    visitTypes?: Array<{ id: number; name: string }>;
    arrivalReasonOverride?: { id: number; name: string } | null;
    onVisitTypeSaved?: (patientId: number, visitType: { id: number; name: string } | null) => void;
    onVisitTypeUpdating?: (updating: boolean) => void;
    activeTabProp?: number;
    onTabChange?: (newTab: number) => void;
}

export default function PatientCard({ data, onAction, t, isolationIconsMap, visitTypes, arrivalReasonOverride, onVisitTypeSaved, onVisitTypeUpdating, activeTabProp, onTabChange }: Readonly<PatientCardProps>) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const mapVitalStatus = (raw: any): { variant: VitalBadgeVariant; label: string } | null => {
        if (raw === null || raw === undefined || raw === "") return null;

        const numericValue = Number(raw);
        if (!Number.isNaN(numericValue)) {
            if (numericValue === 0) return { variant: "good", label: "İyi" };
            if (numericValue === 1) return { variant: "moderate", label: "Orta" };
            if (numericValue === 2) return { variant: "severe", label: "Ağır" };
            if (numericValue === 3) return { variant: "critical", label: "Kritik" };
        }

        const v = String(raw).trim().toLowerCase();
        if (v === "good" || v === "iyi") return { variant: "good", label: "İyi" };
        if (v === "medium" || v === "moderate" || v === "orta") return { variant: "moderate", label: "Orta" };
        if (v === "severe" || v === "ağır" || v === "agir") return { variant: "severe", label: "Ağır" };
        if (v === "critical" || v === "kritik") return { variant: "critical", label: "Kritik" };
        return null;
    };

    const vital = mapVitalStatus((data as any)?.vitalsignstatus);

    // GeliÃƒâ€¦Ã…Â¸ Nedeni dropdown (GetVisitTypes + UpdatePatientVisitType)
    const [arrivalReasonMenuAnchor, setArrivalReasonMenuAnchor] = useState<HTMLElement | null>(null);
    const [arrivalReason, setArrivalReason] = useState<any>(null);
    const [arrivalReasonUpdating, setArrivalReasonUpdating] = useState(false);
    const arrivalReasonOptions = useMemo(() => {
        const list = Array.isArray(visitTypes) ? visitTypes : [];
        return list.map((x) => ({ id: x.id, NAME: x.name }));
    }, [visitTypes]);
    const patientVisitTypeId = (data as any)?.visittypeid;
    const patientVisitTypeName = (data as any)?.visittypename;

    useEffect(() => {
        // kart data gÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¼ncellendikÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â§e seÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â§imi senkronla
        const id = arrivalReasonOverride?.id ?? patientVisitTypeId;
        const nameFromRow = arrivalReasonOverride?.name ?? patientVisitTypeName;
        if (id != null) {
            const foundName =
                (nameFromRow && String(nameFromRow).trim()) ||
                arrivalReasonOptions.find((x) => Number(x.id) === Number(id))?.NAME ||
                "";
            const next = { id: Number(id), NAME: foundName };
            setArrivalReason(next);
        } else {
            setArrivalReason(null);
        }
    }, [arrivalReasonOverride, patientVisitTypeId, patientVisitTypeName, arrivalReasonOptions]);

    const handleArrivalReasonSelect = (item: any) => {
        if (!item || arrivalReasonUpdating) return;

        setArrivalReason(item);
        setArrivalReasonMenuAnchor(null);

        const ptid = Number((data as any)?.ptid ?? data?.id);
        const visittypeid = item?.id != null ? Number(item.id) : null;
        if (!ptid) return;

        setArrivalReasonUpdating(true);
        onVisitTypeUpdating?.(true);

        nurseService.nurse
            .updatePatientVisitType<{ success?: boolean; issuccess?: boolean; isSuccess?: boolean; data?: any }>({
                ptid,
                visittypeid,
            })
            .then((res: any) => {
                const ok =
                    res?.success === true ||
                    res?.issuccess === true ||
                    res?.isSuccess === true ||
                    typeof res?.data === "number";

                if (ok) {
                    onVisitTypeSaved?.(ptid, {
                        id: Number(item.id),
                        name: String(item.NAME ?? ""),
                    });
                }
            })
            .catch(() => {
                // UI secimini koru, sonraki refetch ile veri senkron olur.
            })
            .finally(() => {
                setArrivalReasonUpdating(false);
                onVisitTypeUpdating?.(false);
            });
    };

    const [activeTab, setActiveTab] = useState(activeTabProp ?? 0);
    useEffect(() => {
        if (activeTabProp !== undefined) {
            setActiveTab(activeTabProp);
        }
    }, [activeTabProp]);

    const handleTabSwitch = (newTab: number) => {
        if (onTabChange) {
            onTabChange(newTab);
        } else {
            setActiveTab(newTab);
        }
    };

    const handlePrevTab = () => {
        const nextTab = activeTab === 0 ? 2 : activeTab - 1;
        handleTabSwitch(nextTab);
    };

    const handleNextTab = () => {
        const nextTab = activeTab === 2 ? 0 : activeTab + 1;
        handleTabSwitch(nextTab);
    };

    // Swipe Logic
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null); // Reset touch end
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNextTab(); // Swipe Left -> Next
        }
        if (isRightSwipe) {
            handlePrevTab(); // Swipe Right -> Prev
        }
    };

    // Icon configuration based on the provided design image (12 icons)
    const iconConfig = [
        // Row 1
        { key: "ispregnant", label: t("nurse-icons.pregnant"), imageSrc: "/assets/icons/pages/nurse/pregnant.svg", colorType: "green" },
        { key: "hasbloodrequest", label: t("nurse-icons.blood"), imageSrc: "/assets/icons/pages/nurse/blood-request.svg", colorType: "red" },
        { key: "hasradiology", label: t("nurse-icons.radiology"), imageSrc: "/assets/icons/pages/nurse/radiology.svg", colorType: "green" },
        { key: "hasorder_y", label: t("nurse-icons.order-y"), imageSrc: "/assets/icons/pages/nurse/order-pill-green.svg", colorType: "green" },

        // Row 2
        { key: "hasairway", label: t("nurse-icons.airway"), imageSrc: "/assets/icons/pages/nurse/air-way.svg", colorType: "blue" },
        { key: "consultation", label: t("nurse-icons.consultation"), imageSrc: "/assets/icons/pages/nurse/consultation.svg", colorType: "blue" },
        { key: "haspain", label: t("nurse-icons.pain"), imageSrc: "/assets/icons/pages/nurse/painList/pain-0.svg", colorType: "dynamic" },
        { key: "hasorder_g", label: t("nurse-icons.order-g"), imageSrc: "/assets/icons/pages/nurse/order-pill-green.svg", colorType: "red" },

        // Row 3
        { key: "hasfallrisk", label: t("nurse-icons.fall-risk"), imageSrc: "/assets/icons/pages/nurse/fall-risk.svg", colorType: "red" },
        { key: "hasprosthesis", label: t("nurse.patient-list.detail-grid.protez"), imageSrc: "/assets/icons/pages/nurse/prosthesis.svg", colorType: "grey" },
        { key: "hasration", label: t("nurse.patient-list.detail-grid.diet"), imageSrc: "/assets/icons/pages/nurse/diet.svg", colorType: "multi" },
        { key: "hasorder_d", label: t("nurse-icons.order-d"), imageSrc: "/assets/icons/pages/nurse/order-pill-green.svg", colorType: "orange" },
    ];

    const labelStyle = {
        color: '#767a85',
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: '16px',
        mb: 0.25
    };

    const valueStyle = {
        color: '#1f2026',
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '18px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontFamily: 'Poppins, sans-serif'
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return (
                    <Grid container columnSpacing={1} rowSpacing={1.3} sx={{ mt: 0, width: '100%', minWidth: 0 }}>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.card-view.file-protocol") || "Dosya / Protokol"}</Typography>
                            <Typography sx={valueStyle} noWrap>{data.upn ?? "-"} / {data.protid ?? "-"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.card-view.reason-for-arrival") || "Geliş Nedeni"}</Typography>
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (arrivalReasonUpdating) return;
                                    setArrivalReasonMenuAnchor(e.currentTarget as HTMLElement);
                                }}
                                sx={{
                                    width: "100%",
                                    height: 32,
                                    px: 1.25,
                                    borderRadius: 1,
                                    border: "1px solid #E0E5EB",
                                    bgcolor: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    cursor: arrivalReasonUpdating ? "not-allowed" : "pointer",
                                    opacity: arrivalReasonUpdating ? 0.7 : 1,
                                }}
                            >
                                <Typography sx={valueStyle} noWrap>
                                    {arrivalReason?.NAME || "-"}
                                </Typography>
                                <ArrowDropDownIcon sx={{ color: "#8A97A6" }} />
                            </Box>

                            <Menu
                                anchorEl={arrivalReasonMenuAnchor}
                                open={Boolean(arrivalReasonMenuAnchor)}
                                onClose={() => setArrivalReasonMenuAnchor(null)}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                                onClick={(e) => e.stopPropagation()}
                                PaperProps={{
                                    sx: {
                                        mt: 0.75,
                                        minWidth: isMobile ? 0 : 148,
                                        width: isMobile ? "calc(100vw - 48px)" : undefined,
                                        maxWidth: isMobile ? "calc(100vw - 48px)" : 280,
                                        borderRadius: "12px",
                                        border: "1px solid #E0E5EB",
                                        boxShadow: "0px 14px 32px rgba(31, 41, 55, 0.14)",
                                        overflow: "hidden",
                                        "& .MuiList-root": {
                                            py: 0.5,
                                        },
                                    },
                                }}
                            >
                                {arrivalReasonOptions.map((option) => (
                                    <MenuItem
                                        key={option.id}
                                        selected={Number(arrivalReason?.id) === Number(option.id)}
                                        disabled={arrivalReasonUpdating}
                                        onClick={() => handleArrivalReasonSelect(option)}
                                        sx={{
                                            minHeight: 36,
                                            px: 2,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            color: "#3B4252",
                                        }}
                                    >
                                        {option.NAME}
                                    </MenuItem>
                                ))}
                                <MenuItem
                                    onClick={() => setArrivalReasonMenuAnchor(null)}
                                    sx={{
                                        minHeight: 38,
                                        px: 2,
                                        mt: 0.5,
                                        borderTop: "1px solid #EEF2F6",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#30A4E0",
                                    }}
                                >
                                    Kapat
                                </MenuItem>
                            </Menu>
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.room-bed") || "Oda/Yatak"}</Typography>
                            <Typography sx={valueStyle} noWrap>{data.bedinfo ?? "-"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.diagnosis") || "Tanı"}</Typography>
                            <Tooltip title={data.diseasecodes || ""}>
                                <Typography sx={valueStyle} noWrap>
                                    {data.diseasecodes || "-"}
                                </Typography>
                            </Tooltip>
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("gender")} / {t("nurse.patient-list.columns.age")}</Typography>
                            <Typography sx={valueStyle} noWrap>{`${data.sexuality ?? "-"} / ${data.age ?? "-"}`}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.blood-group") || "Kan Grubu"}</Typography>
                            <Typography sx={valueStyle} noWrap>{data.bloodgroup ?? "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.department") || "Bölüm"}</Typography>
                            <Typography sx={valueStyle} noWrap>{data.deptname ?? "-"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.doctor") || "Doktoru"}</Typography>
                            <Typography sx={valueStyle} noWrap>{data.drname ?? "-"}</Typography>
                        </Grid>
                    </Grid>
                );
            case 1:
                return (
                    <Grid container columnSpacing={1} rowSpacing={1.3} sx={{ mt: 0, width: '100%', minWidth: 0 }}>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.hospitalization-time")}</Typography>
                            <Typography sx={valueStyle}>{data.hospitalizationtime ?? "-"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.discharge-time")}</Typography>
                            <Typography sx={valueStyle}>{data.dischargetime ?? "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.hospitalization-days") || "Yatış Günü"}</Typography>
                            <Typography sx={valueStyle}>{data.hospitalizationduration ? `${data.hospitalizationduration}. ${t("day")}` : "-"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.discharge-type") || "Çıkış Şekli"}</Typography>
                            <Typography sx={valueStyle}>-</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.institution") || "Kurum Bilgisi"}</Typography>
                            <Typography sx={valueStyle}>{data.assname ?? "-"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.companion") || "Refakatçi"}</Typography>
                            <Typography sx={valueStyle}>{data.hascompanion === 1 ? t("exists") : "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.secondary-institution") || "Ek Kurum Bilgisi"}</Typography>
                            <Typography sx={valueStyle}>{data.secondaryassname ?? "-"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography sx={labelStyle} noWrap>{t("nurse.patient-list.columns.psychologist-info") || "Psikolog Bilgisi"}</Typography>
                            <Typography sx={valueStyle}>{data.psychologist ?? "-"}</Typography>
                        </Grid>
                    </Grid>
                );
            case 2:
                const iconGroups = [];
                for (let i = 0; i < iconConfig.length; i += 4) {
                    iconGroups.push(iconConfig.slice(i, i + 4));
                }
                return (
                    <Box sx={{ width: '100%' }}>
                        {iconGroups.map((group, gIdx) => (
                            <Stack
                                key={gIdx}
                                direction="row"
                                spacing={isMobile ? 1 : 0}
                                sx={{ mb: 2, width: '100%', flexWrap: 'wrap', rowGap: 1 }}
                                justifyContent="space-around"
                            >
                                {group.map((item) => {
                                    let val = (data as any)[item.key] ?? (data as any)[item.key.toLowerCase()];
                                    const isActive = item.key === 'haspain'
                                        ? (val != null && val > 0)
                                        : (val === 1 || val === true || val === "1" || val === "true" || (item.key === 'consultation' && val > 0) || (item.key.startsWith('hasorder_') && val > 0));

                                    let iconSrc = item.imageSrc;
                                    if (item.key === 'haspain' && isActive) iconSrc = getPainIcon(val, item.imageSrc);

                                    const redFilter = 'invert(27%) sepia(91%) saturate(5412%) hue-rotate(345deg) brightness(91%) contrast(100%)';
                                    const greenFilter = 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)';
                                    const blueFilter = 'invert(37%) sepia(94%) saturate(1914%) hue-rotate(200deg) brightness(98%) contrast(105%)';
                                    const yellowFilter = 'invert(76%) sepia(85%) saturate(542%) hue-rotate(3deg) brightness(103%) contrast(101%)';
                                    const orangeFilter = 'invert(65%) sepia(93%) saturate(1214%) hue-rotate(355deg) brightness(101%) contrast(106%)';
                                    const greyFilter = 'invert(52%) sepia(9%) saturate(634%) hue-rotate(186deg) brightness(91%) contrast(91%)';

                                    const activeFilter = item.colorType === 'red' ? redFilter :
                                        item.colorType === 'green' ? greenFilter :
                                            item.colorType === 'blue' ? blueFilter :
                                                item.colorType === 'yellow' ? yellowFilter :
                                                    item.colorType === 'orange' ? orangeFilter :
                                                        item.colorType === 'grey' ? greyFilter :
                                                            'none';

                                    return (
                                        <Tooltip key={item.key} title={item.label}>
                                            <Box
                                                sx={{
                                                    width: isMobile ? "calc(25% - 8px)" : 48,
                                                    minWidth: isMobile ? 44 : 48,
                                                    height: 32,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '100px',
                                                    backgroundColor: isActive ? 'rgba(244, 247, 254, 1)' : 'rgba(244, 247, 254, 0.4)',
                                                    filter: isActive ? 'none' : 'grayscale(100%) opacity(0.6)',
                                                    transition: 'all 0.3s',
                                                    '& img': {
                                                        width: 20,
                                                        height: 20,
                                                        filter: (isActive && item.colorType !== 'dynamic' && item.colorType !== 'multi') ? activeFilter : 'none'
                                                    }
                                                }}
                                            >
                                                <img src={iconSrc} alt={item.label} />
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Stack>
                        ))}
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.07)",
                p: 2,
                height: "100%",
                width: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                border: "1px solid #e6e6e6",
                fontFamily: 'Poppins, sans-serif'
            }}
        >
            {/* Header Area */}
            <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                mb={1}
            >
                {/* Avatar Style Button */}
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        onAction("add", data);
                    }}
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "20px",
                        border: "1px solid #e6e6e6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        flexShrink: 0,
                        backgroundColor: '#fff',
                        transition: "all 0.2s",
                        "&:hover": { bgcolor: "#f9f9f9" }
                    }}
                >
                    <AddMaster style={{ width: 24, height: 24, color: '#C3CAD9' }} />
                </Box>

                {/* Name and Icons Column */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 0.5,
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            noWrap
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1f2026',
                                lineHeight: '22px',
                                letterSpacing: '-0.01em',
                                minWidth: 0,
                            }}
                        >
                            {data.name} {data.surname}
                        </Typography>

                        <Box
                            onClick={() => onAction("vital-signs", data)}
                            sx={{ display: "flex", alignItems: "center", flexShrink: 0, cursor: "pointer" }}
                        >
                            {vital ? (
                                <VitalBadge variant={vital.variant} label={vital.label} />
                            ) : (
                                <Box
                                    sx={{
                                        width: "max-content",
                                        borderRadius: "8px",
                                        boxSizing: "border-box",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        px: "8px",
                                        py: "2px",
                                        textAlign: "right",
                                        fontSize: "10px",
                                        fontFamily: "Poppins, sans-serif",
                                        letterSpacing: "-0.02em",
                                        lineHeight: "150%",
                                        border: "1px solid #000000",
                                        bgcolor: "#FFFFFF",
                                        color: "#000000",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <Box component="span">Vital Bulgular</Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Icons row: left = isolation/status (clamped), right = notes fixed to right */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        height={24}
                        spacing={1}
                    >
                        {/* Left: isolation + other status icons with max width */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                maxWidth: "calc(100% - 56px)",
                                overflow: "hidden",
                                flexShrink: 1,
                            }}
                        >
                            {data.isolationtypeids && Array.isArray(data.isolationtypeids) && data.isolationtypeids.map((id) => {
                                const svg = isolationIconsMap[Number(id)];
                                if (!svg) return null;
                                return (
                                    <Box
                                        key={`iso-${id}`}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "24px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: 24,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <img src={`data:image/svg+xml;base64,${svg}`} alt="" style={{ width: 16, height: 16 }} />
                                    </Box>
                                );
                            })}
                            {(data.hasallergy === 1 || data.hasallergy === true || data.hasallergy === "1" || data.hasallergy === "true") && (
                                <img
                                    src="/assets/icons/pages/nurse/alergy.svg"
                                    alt="allergy"
                                    style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: "24px",
                                        filter: "sepia(100%) saturate(500%) hue-rotate(-50deg)",
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            {iconConfig
                                .filter((item) => !["ispregnant", "hasallergy"].includes(item.key))
                                .map((item) => {
                                    const val = (data as any)[item.key];
                                    const isActive =
                                        item.key === "haspain"
                                            ? val != null && val > 0
                                            : val === 1 ||
                                              val === true ||
                                              val === "1" ||
                                              val === "true" ||
                                              (item.key === "consultation" && val > 0);

                                    if (!isActive) return null;

                                    let iconSrc = item.imageSrc;
                                    if (item.key === "haspain") iconSrc = getPainIcon(val, item.imageSrc);

                                    const redFilter = "sepia(100%) saturate(500%) hue-rotate(-50deg)";
                                    const greenFilter =
                                        "invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)";
                                    const activeFilter = item.colorType === "red" ? redFilter : item.colorType === "green" ? greenFilter : "none";

                                    return (
                                        <img
                                            key={`header-${item.key}`}
                                            src={iconSrc}
                                            alt={item.label}
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "24px",
                                                filter:
                                                    item.colorType !== "dynamic" && item.colorType !== "multi"
                                                        ? activeFilter
                                                        : "none",
                                                flexShrink: 0,
                                            }}
                                        />
                                    );
                                })}
                        </Box>

                        {/* Right: notes icons fixed on the right */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexShrink: 0,
                            }}
                        >
                            <Tooltip title="Yapay Zeka Analizi">
                                <Box
                                    component="img"
                                    src="/assets/icons/nurse/ai-nurse-icon.png"
                                    alt="ai-analysis"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAction("ai-analysis", data);
                                    }}
                                    sx={{
                                        width: 18,
                                        height: 18,
                                        cursor: "pointer",
                                        objectFit: "contain",
                                        display: "block",
                                        transition: "transform 0.2s ease, opacity 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-1px)",
                                            opacity: 0.9,
                                        },
                                    }}
                                />
                            </Tooltip>
                            <Box
                                component="img"
                                src="/assets/icons/nurse/note-view.svg"
                                alt="note-view"
                                sx={{
                                    width: 18,
                                    height: 18,
                                    cursor: "pointer",
                                    filter: "none",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAction("note-view", data);
                                }}
                            />
                            <Box
                                component="img"
                                src="/assets/icons/nurse/note-nurse.svg"
                                alt="note-nurse"
                                sx={{
                                    width: 18,
                                    height: 18,
                                    cursor: "pointer",
                                    filter: (data as any)?.ishavespecialnotes === 1 ? "invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)" : "none",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAction("note-nurse", data);
                                }}
                            />
                        </Box>
                    </Stack>
                </Box>
            </Stack>

            <Divider sx={{ mb: 0.5, borderColor: '#e6e6e6', mx: -2 }} />

            {/* Body Content with Navigation Arrows in same row */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    flexGrow: 1,
                    position: "relative",
                    minHeight: isMobile ? 260 : 220,
                    width: '100%',
                    minWidth: 0,
                    px: isMobile ? 0 : 0.5
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Left Arrow */}
                <IconButton
                    onClick={(e) => { e.stopPropagation(); handlePrevTab(); }}
                    sx={{
                        color: '#767a85',
                        p: 0,
                        mr: isMobile ? 0.25 : 0.5,
                        alignSelf: isMobile ? "flex-start" : "center",
                        '&:hover': { color: '#1f2026', backgroundColor: 'transparent' }
                    }}
                    size="small"
                >
                    <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
                </IconButton>

                {/* Content Area */}
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: activeTab === 2 && !isMobile ? 'center' : 'flex-start' }}>
                    <Box sx={{ width: '100%' }}>
                        {renderTabContent()}
                    </Box>
                </Box>

                {/* Right Arrow */}
                <IconButton
                    onClick={(e) => { e.stopPropagation(); handleNextTab(); }}
                    sx={{
                        color: '#767a85',
                        p: 0,
                        ml: isMobile ? 0.25 : 0.5,
                        alignSelf: isMobile ? "flex-start" : "center",
                        '&:hover': { color: '#1f2026', backgroundColor: 'transparent' }
                    }}
                    size="small"
                >
                    <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Stack>

            {/* Progress Bar Style Indicators */}
            <Stack direction="row" spacing={1} mb={2} sx={{ px: 0.5 }}>
                {[0, 1, 2].map((i) => (
                    <Box
                        key={i}
                        onClick={() => handleTabSwitch(i)}
                        sx={{
                            height: 5,
                            flex: 1,
                            borderRadius: '100px',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            bgcolor: i === activeTab ? '#2196f3' : '#ededed',
                            border: i === activeTab ? '1px solid rgba(33, 150, 243, 0.2)' : '1px solid rgba(237, 237, 237, 0.2)',
                            boxSizing: 'border-box'
                        }}
                    />
                ))}
            </Stack>

            {/* Footer Actions */}
            <Box sx={{ borderTop: '1px solid #e6e6e6', mx: -2, pt: 1.5, px: 2 }}>
                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                    <Box
                        onClick={() => onAction("treatments", data)}
                        sx={{
                            flex: { xs: "1 1 calc(50% - 6px)", sm: 1 },
                            minWidth: 0,
                            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.07)",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            border: "1px solid #e6e6e6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "9px 8px",
                            cursor: "pointer",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#484d58',
                            textAlign: 'center',
                            transition: "all 0.2s",
                            "&:hover": { bgcolor: "#f9f9f9" }
                        }}
                    >
                        {t("nurse.patient-list.card-view.treatments") || "Tedaviler"}
                    </Box>
                    <Box
                        onClick={() => onAction("patient-table", data)}
                        sx={{
                            flex: { xs: "1 1 calc(50% - 6px)", sm: 1 },
                            minWidth: 0,
                            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.07)",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            border: "1px solid #e6e6e6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "9px 8px",
                            cursor: "pointer",
                            fontSize: '12px',
                            fontWeight: 600,
                            lineHeight: '16px',
                            textAlign: 'center',
                            color: '#484d58',
                            transition: "all 0.2s",
                            "&:hover": { bgcolor: "#f9f9f9" }
                        }}
                    >
                        {"Hasta Tabelası"}
                    </Box>
                    <Box
                        onClick={() => onAction("patient-processes", data)}
                        sx={{
                            flex: { xs: "1 1 100%", sm: 1 },
                            minWidth: 0,
                            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.07)",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            border: "1px solid #e6e6e6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "9px 8px",
                            cursor: "pointer",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#484d58',
                            textAlign: 'center',
                            transition: "all 0.2s",
                            "&:hover": { bgcolor: "#f9f9f9" }
                        }}
                    >
                        {t("nurse.patient-list.detail-grid.patient-processes") || "İşlemler"}
                    </Box>
                </Stack>
            </Box>

        </Box>
    );
}



